import React from 'react'
import { useS4LContext } from '@s4l/react-context'
import { Typography, Button } from '@s4l/ds'

import { IconInstagram } from '@s4l/icons/IconInstagram'
import { IconTiktok } from '@s4l/icons/IconTiktok'
import { IconLinkedin } from '@s4l/icons/IconLinkedin'

import { SOCIAL_PAGES } from './constants'
import styles from './styles.module.css'
import { withErrorBoundary } from '@s4l/utils/hocs/withErrorBoundary'

const footerLinksData = [
  { id: 'aboutUs', href: '/the-brand/home', labelId: 'global.about-stayforlong' },
  { id: 'flashsales', href: '/landings/flashsales', labelId: 'global.flash-sales' },
  { id: 'jobs', href: '/the-brand/open-positions', labelId: 'general.jobs' },
  { id: 'reviews', href: '/reviews', labelId: 'global.reviews' },
  { id: 'privacy', href: '/privacy', labelId: 'global.privacy' },
  { id: 'generalConditions', href: '/general-conditions', labelId: 'legal.terms' },
  { id: 'cookies', href: '/cookies', labelId: 'legal.cookies' },
]
export interface IFooterProps {
  onLinkClick?: (linkId: string) => void
  onSocialClick?: (socialId: string) => void
  className?: string
  faqsUrl?: string
}

export interface ISocialLink {
  id: string
  url: string
  icon: React.ReactNode
  label: string
}

export interface IFooterLink {
  id: string
  href: string
  label: string
  isExternal?: boolean
}

export function Footer({ onLinkClick, onSocialClick, className, faqsUrl }: Readonly<IFooterProps>) {
  const {
    i18n: { formatMessage },
  } = useS4LContext()
  const year = new Date().getFullYear()

  const handleLinkClick = (linkId: string) => {
    onLinkClick?.(linkId)
  }

  const handleSocialClick = (socialId: string) => {
    onSocialClick?.(socialId)
  }

  const footerLinks: IFooterLink[] = footerLinksData.map(({ labelId, ...rest }) => ({
    ...rest,
    label: formatMessage({ id: labelId }),
  }))

  if (faqsUrl) {
    footerLinks.push({
      id: 'faqs',
      href: faqsUrl,
      label: formatMessage({ id: 'support.faqs' }),
      isExternal: true,
    })
  }

  const socialLinks: ISocialLink[] = [
    {
      id: 'instagram',
      url: SOCIAL_PAGES.instagram,
      icon: <IconInstagram />,
      label: 'Instagram',
    },
    {
      id: 'tiktok',
      url: SOCIAL_PAGES.tiktok,
      icon: <IconTiktok />,
      label: 'TikTok',
    },
    {
      id: 'linkedin',
      url: SOCIAL_PAGES.linkedin,
      icon: <IconLinkedin />,
      label: 'LinkedIn',
    },
  ]

  return (
    <div className={`${styles.footer} ${className || ''}`} data-testid="footer">
      <div className={styles.mainBlock}>
        <div className={styles.section}>
          <ul className={styles.footerLinks}>
            {footerLinks.map(link => (
              <li key={link.id}>
                <Button
                  as="a"
                  href={link.href}
                  onClick={() => handleLinkClick(link.id)}
                  variant="text-flat"
                  size="large"
                  target={link.isExternal ? '_blank' : '_self'}
                >
                  {link.label}
                </Button>
              </li>
            ))}
          </ul>

          <ul className={styles.socialLinksList}>
            {socialLinks.map(social => (
              <li key={social.id}>
                <Button
                  as="a"
                  href={social.url}
                  onClick={() => handleSocialClick(social.id)}
                  variant="icon-flat"
                  size="small"
                  leadingIcon={social.icon as React.ReactElement}
                  title={social.label}
                  target="_blank"
                />
              </li>
            ))}
          </ul>
        </div>

        <ul className={styles.section}>
          <Typography as="li" variant="body" size="m" color="var(--color-neutral-100)">
            {formatMessage({ id: 'legal.copyright' }, { year })}
          </Typography>
        </ul>
      </div>
    </div>
  )
}

Footer.displayName = 'Footer'
export default withErrorBoundary(Footer)
